import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { Button, Input, makeStyles, mergeClasses, MessageBar, MessageBarBody, Text } from '@fluentui/react-components'
import { InputProps } from '@fluentui/react-input'
import { TextProps } from '@fluentui/react-text'
import LoadingIcon from '../../../components/Icon/LoadingIcon'
import {
    ArrowHookUpLeftFilled,
    ArrowHookUpLeftRegular,
    bundleIcon,
    RenameFilled,
    RenameRegular,
    SaveFilled,
    SaveRegular,
} from '@fluentui/react-icons'
import { useApplicationStyles } from '../../../pages/App'
import { normalizeAxiosError } from '../../../reducers/errorReducer'

const useStyles = makeStyles({
    text: {
        height: '30px',
        alignContent: 'center',
    },
})

const RenameIcon = bundleIcon(RenameFilled, RenameRegular)
const BackIcon = bundleIcon(ArrowHookUpLeftFilled, ArrowHookUpLeftRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)

type InlineSelfSubmittingInputProps = {
    value: string //The value to edit.
    onSubmit: (value: string) => Promise<any> //Callback to use when user presses Enter or clicks on Save icon.
    validate?: (value: string) => string | undefined //Returns an error message to block submitting, or undefined to allow it.
    warning?: (value: string) => string | undefined //Returns a live (non-blocking) warning message for the current value.
    getEditSelection?: (value: string) => [number, number] //Selection range applied when entering edit mode. Defaults to the entire value.

    textProps?: TextProps //Props to be applied to the Text component.
    inputProps?: Omit<InputProps, 'value' | 'onChange'> //Props to be applied to the Input component.

    disabled?: boolean //Whether this component should be disabled.
    isEditing?: boolean //State to indicate this component is in Edit mode. Not used internally, but for external use.
    setIsEditing?: (isSubmitting: boolean) => void | Promise<any> //Setter to have component indicate that this component is in Edit mode. Not used internally, but for external use.
    isSubmitting?: boolean //State to indicate this component is Saving. Not used internally, but for external use.
    setIsSubmitting?: (isSubmitting: boolean) => void | Promise<any> //Setter to have component indicate that this component is Saving. Not used internally, but for external use.
}

export default function InlineSelfSubmittingInput(props: InlineSelfSubmittingInputProps): ReactElement {
    const appStyles = useApplicationStyles()
    const styles = useStyles()

    const inputRef = useRef<HTMLInputElement>(null)
    const [value, setValue] = useState(props.value)
    const [previousValue, setPreviousValue] = useState(props.value)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [error, setError] = useState<string>(undefined)

    const warning = isEditing ? props.warning?.(value) : undefined

    const submit = async () => {
        if (isSubmitting) {
            return
        }

        const validationError = props.validate?.(value)
        if (validationError) {
            setError(validationError)
            return
        }

        try {
            setIsSubmitting(true)
            props.setIsSubmitting?.(true)
            await props.onSubmit(value)

            setIsEditing(false)
            props.setIsEditing?.(false)
            setError(undefined)
            setPreviousValue(value)
        } catch (e) {
            console.error(e)
            const error = normalizeAxiosError(e)
            setError(`${error.code}: ${error.message}`)
        } finally {
            setIsSubmitting(false)
            props.setIsSubmitting?.(false)
        }
    }

    useEffect(() => {
        setIsSubmitting(false)
        props.setIsSubmitting?.(false)
        setIsEditing(false)
        props.setIsEditing?.(false)

        setError(undefined)
        setValue(props.value)
        setPreviousValue(props.value)
    }, [props.value])

    useEffect(() => {
        // Focus from an effect: the ref is guaranteed to be attached by then, unlike during an
        // autofocus, where a ref-based select would silently do nothing.
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            const [start, end] = props.getEditSelection?.(value) ?? [0, value?.length ?? 0]
            inputRef.current.setSelectionRange(start, end)
        }
    }, [isEditing])

    if (isEditing) {
        return (
            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexColumn)}>
                <Input
                    {...props.inputProps}
                    ref={inputRef}
                    value={value}
                    onChange={(_, data) => {
                        setValue(data.value)
                        setError(undefined)
                    }}
                    contentBefore={
                        isSubmitting ? (
                            <LoadingIcon />
                        ) : (
                            (props.inputProps?.contentBefore ?? (
                                <Button
                                    size={'small'}
                                    appearance={'subtle'}
                                    icon={<BackIcon />}
                                    onClick={() => {
                                        setValue(previousValue)
                                        setError(undefined)

                                        setIsEditing(false)
                                        props.setIsEditing?.(false)
                                    }}
                                />
                            ))
                        )
                    }
                    contentAfter={
                        props.inputProps?.contentAfter ?? (
                            <Button size={'small'} appearance={'subtle'} icon={<SaveIcon />} onClick={async () => submit()} />
                        )
                    }
                    onKeyDown={async e => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            await submit()
                        }
                    }}
                />
                {warning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{warning}</MessageBarBody>
                    </MessageBar>
                )}
                {error && (
                    <MessageBar intent={'error'}>
                        <MessageBarBody>{error}</MessageBarBody>
                    </MessageBar>
                )}
            </div>
        )
    }

    return (
        <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
            {!isEditing && (
                <Button
                    size={'small'}
                    appearance={'subtle'}
                    icon={<RenameIcon />}
                    onClick={() => {
                        setPreviousValue(value)

                        setIsEditing(true)
                        props.setIsEditing?.(true)
                    }}
                />
            )}
            <Text
                className={styles.text}
                {...props.textProps}
                onDoubleClick={() => {
                    setPreviousValue(value)

                    setIsEditing(true)
                    props.setIsEditing?.(true)
                }}
            >
                {value}
            </Text>
        </div>
    )
}
